import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { getBmi, diagnosesByBmi } from 'utilities/bmi'
import { STATE_RESPONSES } from 'components/shared/survey-question/insurance-covered-loading'
import { stringifyDate } from 'utilities/dates'
import { parseDate } from 'utilities/dates'

export interface SurveyQuestionResponse {
  id?: number
  surveyQuestionId: number
  surveyQuestionResponseId?: number
  responseValue?: string | null
  metadata?: Record<string, any>
}

interface SurveyStore {
  // State
  // Store responses as arrays to support multiple responses per question
  // Key is questionId as string, value is array of SurveyQuestionResponse
  answers: Record<string, SurveyQuestionResponse[]>
  errors: Record<string, string | undefined>
  touched: Record<string, boolean> // Track if field has been touched/blurred
  isSubmitting: boolean
  isInitialized: boolean // Track if responses have been loaded from database
  kickoffCallScheduledAt: number | string | null // Scheduled kickoff call timestamp
  // Insurance coverage flow state
  eligibilityStatus: string | null
  eligibilityData: any
  copay: number | null
  policyMetadata: any
  policyRetryCount: number | null
  selectedPayer: any
  eligibilityInputParams: {
    payerId: string | null
    memberId: string | null
    skipMembershipId: boolean | null
    state: string | null
    firstName: string | null
    lastName: string | null
    dateOfBirth: string | null
    diagnoses: string[]
  } | null
  latestEligibilityParamsFromDB: any | null // Latest eligibilityParams from user.client.latestClientEligibilityStatus

  // Actions
  setAnswer: (questionId: number | string, value: any) => void
  setSkipValue: (questionId: number | string, skipValue: boolean) => void
  setMetadata: (
    questionId: number | string,
    metadata: Record<string, any>
  ) => void
  setError: (questionId: number | string, error: string | null) => void
  setTouched: (questionId: number | string, touched: boolean) => void
  clearAnswer: (questionId: number | string) => void
  clearAllAnswers: () => void
  setSubmitting: (isSubmitting: boolean) => void
  setKickoffCallScheduledAt: (value: number | string | null) => void
  // Insurance coverage flow actions
  setEligibilityStatus: (status: string | null) => void
  setEligibilityData: (data: any) => void
  setCopay: (copay: number | null) => void
  setPolicyMetadata: (metadata: any) => void
  setPolicyRetryCount: (count: number | null) => void
  setSelectedPayer: (payer: any) => void
  setEligibilityInputParams: (
    params: {
      payerId: string | null
      memberId: string | null
      skipMembershipId: boolean | null
      state: string | null
      firstName: string | null
      lastName: string | null
      dateOfBirth: string | null
      diagnoses: string[]
    } | null
  ) => void
  setLatestEligibilityParamsFromDB: (params: any | null) => void
  initializeFromResponses: (responses: SurveyQuestionResponse[]) => void
  updateEligibilityInputParamsFromAnswers: () => void
  initializeKickoffCallScheduledAt: (
    upcomingKickoffCall:
      | { scheduledAt: number | string | Date }
      | null
      | undefined
  ) => void
  markInitialized: () => void

  // Getters
  getAnswer: (questionId: number | string) => any
  getSkipValue: (questionId: number | string) => boolean | undefined
  getError: (questionId: number | string) => string | undefined
  getTouched: (questionId: number | string) => boolean
  hasAnswer: (questionId: number | string) => boolean
  getKickoffCallScheduledAt: () => number | string | null
  // Insurance coverage flow getters
  getEligibilityStatus: () => string | null
  getEligibilityData: () => any
  getCopay: () => number | null
  getPolicyMetadata: () => any
  getPolicyRetryCount: () => number | null
  getSelectedPayer: () => any
}

export const useSurveyStore = create<SurveyStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      answers: {},
      errors: {},
      touched: {},
      isSubmitting: false,
      isInitialized: false,
      kickoffCallScheduledAt: null,
      // Insurance coverage flow initial state
      eligibilityStatus: null,
      eligibilityData: null,
      copay: null,
      policyMetadata: null,
      policyRetryCount: null,
      selectedPayer: null,
      eligibilityInputParams: null,
      latestEligibilityParamsFromDB: null,

      // Set answer value - creates or updates the most recent response
      setAnswer: (questionId, value) => {
        const questionIdStr = String(questionId)
        const questionIdNum = Number(questionId)

        // Relevant question IDs that affect eligibility input params
        const eligibilityRelevantQuestionIds = [
          100, // firstName
          131, // lastName
          11, // dateOfBirth
          1586, // payerId
          20250315, // memberId
          202503192, // state
          5425, // diagnosis
          9, // weight
          10, // height
        ]

        set(
          state => {
            const { [questionIdStr]: _, ...restErrors } = state.errors

            // Handle multiSelect: value is an array of response IDs
            // Create multiple responses (one per selected option)
            if (Array.isArray(value)) {
              const newResponses: SurveyQuestionResponse[] = value.map(
                (responseId: any) => ({
                  surveyQuestionId: questionIdNum,
                  surveyQuestionResponseId:
                    typeof responseId === 'string'
                      ? parseInt(responseId, 10)
                      : responseId,
                })
              )

              return {
                answers: {
                  ...state.answers,
                  [questionIdStr]: newResponses,
                },
                // Clear error when setting answer
                errors: restErrors,
              }
            }

            // For non-array values, update most recent response or create new one
            const existingResponses = state.answers[questionIdStr] || []
            const mostRecentResponse =
              existingResponses[existingResponses.length - 1]

            const updatedResponse: SurveyQuestionResponse = mostRecentResponse
              ? {
                  ...mostRecentResponse,
                  responseValue:
                    value !== null && value !== undefined
                      ? String(value)
                      : null,
                }
              : {
                  surveyQuestionId: questionIdNum,
                  responseValue:
                    value !== null && value !== undefined
                      ? String(value)
                      : null,
                }

            return {
              answers: {
                ...state.answers,
                [questionIdStr]: mostRecentResponse
                  ? [...existingResponses.slice(0, -1), updatedResponse]
                  : [updatedResponse],
              },
              // Clear error when setting answer
              errors: restErrors,
            }
          },
          false,
          `setAnswer: ${questionId}`
        )

        // Update eligibility input params if this question is relevant
        if (eligibilityRelevantQuestionIds.includes(questionIdNum)) {
          get().updateEligibilityInputParamsFromAnswers()
        }
      },

      // Set skip value (for insuranceMembershipId) - updates metadata of most recent response
      setSkipValue: (questionId, skipValue) => {
        const questionIdNum = Number(questionId)

        set(
          state => {
            const questionIdStr = String(questionId)
            const existingResponses = state.answers[questionIdStr] || []
            const mostRecentResponse =
              existingResponses[existingResponses.length - 1]

            if (mostRecentResponse) {
              const updatedResponse: SurveyQuestionResponse = {
                ...mostRecentResponse,
                metadata: {
                  ...mostRecentResponse.metadata,
                  skipMembershipId: skipValue,
                },
              }
              return {
                answers: {
                  ...state.answers,
                  [questionIdStr]: [
                    ...existingResponses.slice(0, -1),
                    updatedResponse,
                  ],
                },
              }
            } else {
              // Create new response with skip value
              const newResponse: SurveyQuestionResponse = {
                surveyQuestionId: questionIdNum,
                responseValue: null,
                metadata: {
                  skipMembershipId: skipValue,
                },
              }
              return {
                answers: {
                  ...state.answers,
                  [questionIdStr]: [newResponse],
                },
              }
            }
          },
          false,
          `setSkipValue: ${questionId}`
        )

        // Update eligibility input params if this is the memberId question (20250315)
        if (questionIdNum === 20250315) {
          get().updateEligibilityInputParamsFromAnswers()
        }
      },

      // Set metadata for a question (e.g., payer object for payer search)
      setMetadata: (questionId, metadata) =>
        set(
          state => {
            const questionIdStr = String(questionId)
            const existingResponses = state.answers[questionIdStr] || []
            const mostRecentResponse =
              existingResponses[existingResponses.length - 1]

            if (mostRecentResponse) {
              const updatedResponse: SurveyQuestionResponse = {
                ...mostRecentResponse,
                metadata: {
                  ...mostRecentResponse.metadata,
                  ...metadata,
                },
              }
              return {
                answers: {
                  ...state.answers,
                  [questionIdStr]: [
                    ...existingResponses.slice(0, -1),
                    updatedResponse,
                  ],
                },
              }
            } else {
              // Create new response with metadata
              const newResponse: SurveyQuestionResponse = {
                surveyQuestionId: Number(questionId),
                metadata,
              }
              return {
                answers: {
                  ...state.answers,
                  [questionIdStr]: [newResponse],
                },
              }
            }
          },
          false,
          `setMetadata: ${questionId}`
        ),

      // Set error for a question
      setError: (questionId, error) =>
        set(
          state => {
            const questionIdStr = String(questionId)
            if (error) {
              return {
                errors: {
                  ...state.errors,
                  [questionIdStr]: error,
                },
              }
            } else {
              const { [questionIdStr]: _, ...restErrors } = state.errors
              return {
                errors: restErrors,
              }
            }
          },
          false,
          `setError: ${questionId}`
        ),

      // Set touched state for a question
      setTouched: (questionId, touched) =>
        set(
          state => ({
            touched: {
              ...state.touched,
              [String(questionId)]: touched,
            },
          }),
          false,
          `setTouched: ${questionId}`
        ),

      // Clear answer for a question
      clearAnswer: questionId =>
        set(
          state => {
            const { [String(questionId)]: _, ...restAnswers } = state.answers
            const { [String(questionId)]: __, ...restErrors } = state.errors
            const { [String(questionId)]: ___, ...restTouched } = state.touched
            return {
              answers: restAnswers,
              errors: restErrors,
              touched: restTouched,
            }
          },
          false,
          `clearAnswer: ${questionId}`
        ),

      // Clear all answers
      clearAllAnswers: () =>
        set(
          {
            answers: {},
            errors: {},
            touched: {},
          },
          false,
          'clearAllAnswers'
        ),

      // Set submitting state
      setSubmitting: isSubmitting =>
        set({ isSubmitting }, false, `setSubmitting: ${isSubmitting}`),

      // Set kickoff call scheduled at
      setKickoffCallScheduledAt: value =>
        set(
          { kickoffCallScheduledAt: value },
          false,
          `setKickoffCallScheduledAt: ${value}`
        ),

      // Initialize kickoff call scheduled at from upcomingKickoffCall
      initializeKickoffCallScheduledAt: upcomingKickoffCall => {
        if (upcomingKickoffCall?.scheduledAt) {
          const scheduledAt = upcomingKickoffCall.scheduledAt
          // Convert Date to timestamp if needed
          const timestamp =
            scheduledAt instanceof Date
              ? scheduledAt.getTime()
              : typeof scheduledAt === 'string'
              ? Number(scheduledAt)
              : scheduledAt
          set(
            { kickoffCallScheduledAt: timestamp },
            false,
            'initializeKickoffCallScheduledAt'
          )
        }
      },

      // Get answer value - returns the value from the most recent response
      // For multiSelect, returns array of surveyQuestionResponseIds
      getAnswer: questionId => {
        const responses = get().answers[String(questionId)]
        if (!responses || responses.length === 0) return undefined

        // If multiple responses exist for the same questionId, it's likely a multiSelect
        // Return array of surveyQuestionResponseIds
        if (responses.length > 1) {
          return responses
            .map(r => r.surveyQuestionResponseId)
            .filter(id => id !== undefined && id !== null)
        }

        // Single response - return responseValue or surveyQuestionResponseId
        const mostRecent = responses[responses.length - 1]
        return mostRecent?.responseValue ?? mostRecent?.surveyQuestionResponseId
      },

      // Get skip value - checks metadata of most recent response
      getSkipValue: questionId => {
        const responses = get().answers[String(questionId)]
        if (!responses || responses.length === 0) return undefined
        const mostRecent = responses[responses.length - 1]
        return mostRecent?.metadata?.skipMembershipId ?? false
      },

      // Get error
      getError: questionId => {
        return get().errors[String(questionId)]
      },

      // Get touched state
      getTouched: questionId => {
        return get().touched[String(questionId)] || false
      },

      // Check if question has answer
      hasAnswer: questionId => {
        const responses = get().answers[String(questionId)]
        if (!responses || responses.length === 0) return false
        const mostRecent = responses[responses.length - 1]
        return (
          (mostRecent?.responseValue !== undefined &&
            mostRecent?.responseValue !== null) ||
          mostRecent?.surveyQuestionResponseId !== undefined
        )
      },

      // Get kickoff call scheduled at
      getKickoffCallScheduledAt: () => {
        return get().kickoffCallScheduledAt
      },

      // Set eligibility status
      setEligibilityStatus: status =>
        set(
          { eligibilityStatus: status },
          false,
          `setEligibilityStatus: ${status}`
        ),

      // Set eligibility data
      setEligibilityData: data =>
        set({ eligibilityData: data }, false, 'setEligibilityData'),

      // Set copay
      setCopay: copayValue =>
        set({ copay: copayValue }, false, `setCopay: ${copayValue}`),

      // Set policy metadata
      setPolicyMetadata: metadata =>
        set({ policyMetadata: metadata }, false, 'setPolicyMetadata'),

      // Set policy retry count
      setPolicyRetryCount: count =>
        set(
          { policyRetryCount: count },
          false,
          `setPolicyRetryCount: ${count}`
        ),

      // Set selected payer
      setSelectedPayer: payer =>
        set({ selectedPayer: payer }, false, 'setSelectedPayer'),

      // Set eligibility input params (used to detect if data has changed)
      setEligibilityInputParams: params =>
        set(
          { eligibilityInputParams: params },
          false,
          'setEligibilityInputParams'
        ),

      // Set latest eligibility params from database
      setLatestEligibilityParamsFromDB: params =>
        set(
          { latestEligibilityParamsFromDB: params },
          false,
          'setLatestEligibilityParamsFromDB'
        ),

      // Get eligibility status
      getEligibilityStatus: () => {
        return get().eligibilityStatus
      },

      // Get eligibility data
      getEligibilityData: () => {
        return get().eligibilityData
      },

      // Get copay
      getCopay: () => {
        return get().copay
      },

      // Get policy metadata
      getPolicyMetadata: () => {
        return get().policyMetadata
      },

      // Get policy retry count
      getPolicyRetryCount: () => {
        return get().policyRetryCount
      },

      // Get selected payer
      getSelectedPayer: () => {
        return get().selectedPayer
      },

      // Initialize store from database responses
      // Simply groups responses by questionId - no transformation needed
      initializeFromResponses: (responses: SurveyQuestionResponse[]) => {
        const newAnswers: Record<string, SurveyQuestionResponse[]> = {}
        const newTouched: Record<string, boolean> = {}

        // Group responses by questionId
        responses.forEach(response => {
          const questionId = String(response.surveyQuestionId)
          if (!newAnswers[questionId]) {
            newAnswers[questionId] = []
          }
          newAnswers[questionId].push(response)
          // Mark as touched since it came from database
          newTouched[questionId] = true
        })

        set(
          {
            answers: newAnswers,
            touched: newTouched,
            isInitialized: true,
          },
          false,
          'initializeFromResponses'
        )

        // Update eligibility input params after initialization
        get().updateEligibilityInputParamsFromAnswers()
      },

      // Update eligibility input params from current answers
      updateEligibilityInputParamsFromAnswers: () => {
        const store = get()
        const answers = store.answers

        // Helper to get most recent response value for a question
        const getResponseValue = (questionId: number | string) => {
          const responses = answers[String(questionId)]
          if (!responses || responses.length === 0) return null
          const mostRecent = responses[responses.length - 1]
          return (
            mostRecent?.responseValue ??
            mostRecent?.surveyQuestionResponseId ??
            null
          )
        }

        // Helper to get metadata from most recent response
        const getResponseMetadata = (questionId: number | string) => {
          const responses = answers[String(questionId)]
          if (!responses || responses.length === 0) return null
          const mostRecent = responses[responses.length - 1]
          return mostRecent?.metadata ?? null
        }

        // Extract basic fields
        const firstName = getResponseValue(100) as string | null
        const lastName = getResponseValue(131) as string | null
        const payerId = getResponseValue(1586) as string | null
        const memberId = getResponseValue(20250315) as string | null
        const dateOfBirthRaw = getResponseValue(11) as string | null

        // Get skipMembershipId from metadata
        const memberMetadata = getResponseMetadata(20250315)
        const skipMembershipId = memberMetadata?.skipMembershipId ?? null

        // Format dateOfBirth - convert to YYYY-MM-DD format if it's a date
        let dateOfBirth: string | null = null
        if (dateOfBirthRaw) {
          try {
            // If it's already in YYYY-MM-DD format, use it
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirthRaw)) {
              dateOfBirth = dateOfBirthRaw
            } else {
              // Try to parse and format
              const parsedDate = parseDate(dateOfBirthRaw)
              if (parsedDate) {
                dateOfBirth = stringifyDate(parsedDate)
              }
            }
          } catch (e) {
            // If parsing fails, use raw value
            dateOfBirth = dateOfBirthRaw
          }
        }

        // Get state - map from surveyQuestionResponseId to state code
        const stateResponseId = getResponseValue(202503192)
        let state: string | null = null
        if (stateResponseId) {
          const stateId = parseInt(String(stateResponseId))
          state = STATE_RESPONSES[stateId] ?? null
        }

        // Calculate diagnoses from BMI (height and weight)
        const height = getResponseValue(10)
        const weight = getResponseValue(9)
        let diagnoses: string[] = []

        // Calculate BMI if we have both height and weight
        if (height && weight) {
          const heightNum =
            typeof height === 'string' ? parseFloat(height) : height
          const weightNum =
            typeof weight === 'string' ? parseFloat(weight) : weight
          if (!isNaN(heightNum) && !isNaN(weightNum)) {
            const bmi = getBmi({ height: heightNum, weight: weightNum })
            if (bmi) {
              const bmiDiagnoses = diagnosesByBmi({ bmi })
              diagnoses = [...diagnoses, ...bmiDiagnoses]
            }
          }
        }

        // Add diagnoses from question 5425
        const diagnosisResponse = getResponseValue(5425)
        if (diagnosisResponse && typeof diagnosisResponse === 'string') {
          const previouslyAnsweredDiagnoses = diagnosisResponse
            .split(',')
            .filter(Boolean)
          diagnoses = [...diagnoses, ...previouslyAnsweredDiagnoses]
        }

        // Remove duplicates
        diagnoses = Array.from(new Set(diagnoses)) as string[]

        // Update eligibility input params
        store.setEligibilityInputParams({
          payerId,
          memberId,
          skipMembershipId,
          state,
          firstName,
          lastName,
          dateOfBirth,
          diagnoses,
        })
      },

      // Mark store as initialized
      markInitialized: () =>
        set({ isInitialized: true }, false, 'markInitialized'),
    }),
    { name: 'SurveyStore' }
  )
)
